import {
  BASEMAP_COLOUR_GROUPS,
  BASEMAP_SEMANTIC_GROUPS,
  basemapPreset,
  DEFAULT_BASEMAP_CUSTOMIZATION,
  effectiveBasemapCustomization,
  baseEffectiveBasemapCustomization,
  type BasemapConfiguration,
  type BasemapCustomization,
  type CuratedBasemapPresetId,
  type EffectiveBasemapCustomization,
} from './catalog';
import { BASEMAP_LABEL_GROUPS, basemapLabelRecipe } from './labelPresentation';

export const BASEMAP_PRESENTATIONS = [
  { id: 'standard', label: 'Standard', description: 'The family’s normal reference map' },
  { id: 'muted', label: 'Muted', description: 'The same information, quieter' },
  { id: 'minimal', label: 'Minimal', description: 'Essential locator context' },
  { id: 'thematic', label: 'Thematic', description: 'Selected context behind your data' },
] as const;
export type BasemapPresentationId = (typeof BASEMAP_PRESENTATIONS)[number]['id'];

/** Recipes resolve to existing authored values; there is no persistent presentation mode. */
const FAMILY_RECIPES: Readonly<
  Record<
    CuratedBasemapPresetId,
    Record<
      Exclude<BasemapPresentationId, 'standard'>,
      {
        readonly contextOpacity: number;
        readonly labelsOpacity: number;
        readonly colours: EffectiveBasemapCustomization['colours'];
      }
    >
  >
> = {
  'dropmap-light': {
    muted: {
      contextOpacity: 0.82,
      labelsOpacity: 0.78,
      colours: {
        water: '#ced9dc',
        motorwaysHighways: '#d9dcdb',
        arterialRoads: '#e5e7e4',
        collectorRoads: '#ebecea',
        localRoads: '#e9eae7',
        parksGreenSpace: '#e9ede6',
        naturalVegetation: '#e3e8e0',
      },
    },
    minimal: {
      contextOpacity: 0.96,
      labelsOpacity: 0.92,
      colours: {
        water: '#ccd9dd',
        motorwaysHighways: '#cdd2d1',
        arterialRoads: '#dde1df',
        collectorRoads: '#e8ebe7',
        localRoads: '#f0f1ee',
        parksGreenSpace: '#e9eee5',
        naturalVegetation: '#e5ebe1',
      },
    },
    thematic: {
      contextOpacity: 0.88,
      labelsOpacity: 0.82,
      colours: {
        water: '#c9d8de',
        motorwaysHighways: '#d8ddde',
        arterialRoads: '#e2e6e5',
        collectorRoads: '#edf0ec',
        localRoads: '#f2f3f0',
        parksGreenSpace: '#e4ebdf',
        naturalVegetation: '#e0e8da',
      },
    },
  },
  'dropmap-streets': {
    muted: {
      contextOpacity: 0.86,
      labelsOpacity: 0.76,
      colours: {
        water: '#dce6eb',
        motorwaysHighways: '#e8d5b7',
        arterialRoads: '#ece4ce',
        collectorRoads: '#ebebe7',
        localRoads: '#f8f8f5',
        parksGreenSpace: '#e0ebd7',
        naturalVegetation: '#d1e1c6',
      },
    },
    minimal: {
      contextOpacity: 0.96,
      labelsOpacity: 0.9,
      colours: {
        water: '#d5e3e9',
        motorwaysHighways: '#dbd1bd',
        arterialRoads: '#e6e1d2',
        collectorRoads: '#eeeee8',
        localRoads: '#f6f5f0',
        parksGreenSpace: '#e4ebdc',
        naturalVegetation: '#dce6d4',
      },
    },
    thematic: {
      contextOpacity: 0.88,
      labelsOpacity: 0.8,
      colours: {
        water: '#d0e0e7',
        motorwaysHighways: '#d6d8d5',
        arterialRoads: '#e0e3dd',
        collectorRoads: '#eff0e9',
        localRoads: '#f7f6f1',
        parksGreenSpace: '#dfe9d7',
        naturalVegetation: '#d4e2ca',
      },
    },
  },
  'dropmap-dark': {
    muted: {
      contextOpacity: 0.94,
      labelsOpacity: 0.72,
      colours: {
        water: '#1c252e',
        motorwaysHighways: '#454b52',
        arterialRoads: '#363d44',
        collectorRoads: '#2e353c',
        localRoads: '#2a3036',
        parksGreenSpace: '#28342f',
        naturalVegetation: '#26322d',
      },
    },
    minimal: {
      contextOpacity: 0.98,
      labelsOpacity: 0.88,
      colours: {
        water: '#192732',
        motorwaysHighways: '#4b5259',
        arterialRoads: '#373f46',
        collectorRoads: '#2d343b',
        localRoads: '#282d33',
        parksGreenSpace: '#2c3831',
        naturalVegetation: '#28342e',
      },
    },
    thematic: {
      contextOpacity: 0.96,
      labelsOpacity: 0.8,
      colours: {
        water: '#192935',
        motorwaysHighways: '#3e464e',
        arterialRoads: '#303940',
        collectorRoads: '#282f35',
        localRoads: '#252a30',
        parksGreenSpace: '#29382f',
        naturalVegetation: '#25342c',
      },
    },
  },
};

export function basemapPresentationRecipe(
  family: CuratedBasemapPresetId,
  presentation: BasemapPresentationId,
): EffectiveBasemapCustomization {
  const standard = basemapPreset(family).customizationDefaults;
  if (presentation === 'standard') return standard;
  const recipe = FAMILY_RECIPES[family][presentation];
  const visibility = { ...standard.visibility };
  if (presentation === 'minimal' || presentation === 'thematic') {
    Object.assign(visibility, {
      pointsOfInterest: false,
      buildings: false,
      transitLabels: false,
      transitLines: false,
      routeShields: false,
      roadNames: false,
      otherLabels: false,
    });
  }
  if (presentation === 'minimal') {
    Object.assign(visibility, {
      suburbsLocalities: false,
      naturalWaterLabels: false,
      regionalBoundaries: false,
      naturalVegetation: false,
    });
  }
  return { ...recipe, visibility };
}

export function applyBasemapPresentation(
  configuration: BasemapConfiguration,
  presentation: BasemapPresentationId,
): BasemapConfiguration {
  const family = configuration.presetId;
  if (family === 'none') return configuration;
  const values = basemapPresentationRecipe(family, presentation);
  const defaults = basemapPreset(family).customizationDefaults;
  const customization: BasemapCustomization =
    presentation === 'standard'
      ? DEFAULT_BASEMAP_CUSTOMIZATION
      : {
          contextOpacity: values.contextOpacity,
          labelsOpacity: values.labelsOpacity,
          visibility: Object.fromEntries(
            BASEMAP_SEMANTIC_GROUPS.map((group) => [
              group,
              values.visibility[group] === defaults.visibility[group]
                ? null
                : values.visibility[group],
            ]),
          ) as unknown as BasemapCustomization['visibility'],
          colours: { ...values.colours },
        };
  const explicitLabels =
    configuration.labelPresentation && configuration.labelPresentation !== 'follow-basemap';
  const previous = configuration.customizationByPreset[family];
  const linked = basemapLabelRecipe(family, presentation);
  const next = explicitLabels
    ? {
        ...customization,
        labelsOpacity: previous.labelsOpacity,
        visibility: {
          ...customization.visibility,
          ...Object.fromEntries(
            BASEMAP_LABEL_GROUPS.map((group) => [group, previous.visibility[group]]),
          ),
        },
      }
    : {
        ...customization,
        labelsOpacity: linked.opacity === defaults.labelsOpacity ? null : linked.opacity,
        visibility: {
          ...customization.visibility,
          ...Object.fromEntries(
            BASEMAP_LABEL_GROUPS.map((group) => [
              group,
              linked.visibility[group] === defaults.visibility[group]
                ? null
                : linked.visibility[group],
            ]),
          ),
        },
      };
  return {
    ...configuration,
    labelPresentation: configuration.labelPresentation ?? 'follow-basemap',
    customizationByPreset: {
      ...configuration.customizationByPreset,
      [family]: {
        ...next,
        ...(previous.labelAppearance ? { labelAppearance: previous.labelAppearance } : {}),
      },
    },
  };
}

export function basemapPresentationStatus(
  configuration: BasemapConfiguration,
  family = configuration.presetId,
): BasemapPresentationId | 'custom' | null {
  if (family === 'none') return null;
  if (configuration.labelPresentation !== undefined)
    return basemapContextPresentationStatus(configuration, family);
  const values = effectiveBasemapCustomization(configuration, family);
  return (
    BASEMAP_PRESENTATIONS.find(({ id }) => {
      const recipe = basemapPresentationRecipe(family, id);
      return (
        values.contextOpacity === recipe.contextOpacity &&
        ((configuration.labelPresentation &&
          configuration.labelPresentation !== 'follow-basemap') ||
          values.labelsOpacity === recipe.labelsOpacity) &&
        BASEMAP_SEMANTIC_GROUPS.every(
          (group) =>
            (configuration.labelPresentation &&
              configuration.labelPresentation !== 'follow-basemap' &&
              BASEMAP_LABEL_GROUPS.includes(group as (typeof BASEMAP_LABEL_GROUPS)[number])) ||
            values.visibility[group] === recipe.visibility[group],
        ) &&
        BASEMAP_COLOUR_GROUPS.every(
          (group) => values.colours[group].toLowerCase() === recipe.colours[group].toLowerCase(),
        )
      );
    })?.id ?? 'custom'
  );
}

/** Finds the context recipe even when labels have been explicitly overridden. */
export function basemapContextPresentationStatus(
  configuration: BasemapConfiguration,
  family = configuration.presetId,
): BasemapPresentationId | 'custom' | null {
  if (family === 'none') return null;
  const values = baseEffectiveBasemapCustomization(configuration, family);
  return (
    BASEMAP_PRESENTATIONS.find(({ id }) => {
      const recipe = basemapPresentationRecipe(family, id);
      return (
        values.contextOpacity === recipe.contextOpacity &&
        BASEMAP_SEMANTIC_GROUPS.filter(
          (group) => !BASEMAP_LABEL_GROUPS.includes(group as (typeof BASEMAP_LABEL_GROUPS)[number]),
        ).every((group) => values.visibility[group] === recipe.visibility[group]) &&
        BASEMAP_COLOUR_GROUPS.every(
          (group) => values.colours[group].toLowerCase() === recipe.colours[group].toLowerCase(),
        )
      );
    })?.id ?? 'custom'
  );
}
